import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { AlertTriangle, CheckCircle2, Clock, BookOpen } from 'lucide-react';
import { cn } from '../../../../lib/utils';

/**
 * Compliance Reminders Widget
 * Shows jurisdiction-specific statutory deadline rules and compliance requirements
 * Helps DPO understand legal obligations for current DSR's jurisdiction
 */
export function ComplianceRemindersWidget({ jurisdiction = 'JM', dsr = null }) {
  const jurisdictionRules = {
    JM: {
      name: 'Jamaica',
      flag: '🇯🇲',
      standardDays: 30,
      extensionDays: 30,
      maxDays: 60,
      regulator: 'Office of the Information Commissioner',
      law: 'Data Protection Act 2020',
      requirements: [
        'Respond within 30 calendar days of receipt',
        'May extend by 30 days if complex (notify data subject)',
        'Must verify identity of requester',
        'Provide data in commonly used electronic format',
        'No fee unless request is manifestly unfounded'
      ]
    },
    BB: {
      name: 'Barbados',
      flag: '🇧🇧',
      standardDays: 30,
      extensionDays: 60,
      maxDays: 90,
      regulator: 'Data Protection Commissioner',
      law: 'Data Protection Act 2019',
      requirements: [
        'Respond within 30 calendar days of receipt',
        'May extend by up to 60 days for complex requests',
        'Confirm identity of data subject',
        'Provide copies in requested format if possible',
        'Cannot charge unreasonable fees'
      ]
    },
    BM: {
      name: 'Bermuda',
      flag: '🇧🇲',
      standardDays: 45,
      extensionDays: 30,
      maxDays: 75,
      regulator: 'Privacy Commissioner',
      law: 'Personal Information Protection Act 2016',
      requirements: [
        'Respond within 45 calendar days of receipt',
        'May extend by 30 days if necessary',
        'Request must be in writing',
        'Provide information in requested format',
        'Can charge reasonable administrative costs'
      ]
    },
    KY: {
      name: 'Cayman Islands',
      flag: '🇰🇾',
      standardDays: 30,
      extensionDays: 30,
      maxDays: 60,
      regulator: 'Ombudsman',
      law: 'Data Protection Act 2017',
      requirements: [
        'Respond within 30 calendar days of receipt',
        'May extend by 30 days (must notify data subject)',
        'Verify requester identity',
        'Provide data in accessible format',
        'Fee limited to cost of copying and postage'
      ]
    },
    BZ: {
      name: 'Belize',
      flag: '🇧🇿',
      standardDays: 30,
      extensionDays: 60,
      maxDays: 90,
      regulator: 'Information Commissioner',
      law: 'Data Protection Act',
      requirements: [
        'Respond within 30 calendar days of receipt',
        'May extend by up to 60 days for complex cases',
        'Must verify data subject identity',
        'Provide information in requested format',
        'Cannot charge excessive fees'
      ]
    },
    BS: {
      name: 'Bahamas',
      flag: '🇧🇸',
      standardDays: 30,
      extensionDays: 60,
      maxDays: 90,
      regulator: 'Data Protection Commissioner',
      law: 'Data Protection Act 2003',
      requirements: [
        'Respond within 30 calendar days of receipt',
        'May extend by up to 60 days',
        'Confirm identity of requester',
        'Provide data in suitable electronic format',
        'Fee structure must be reasonable'
      ]
    },
    TT: {
      name: 'Trinidad and Tobago',
      flag: '🇹🇹',
      standardDays: 30,
      extensionDays: 30,
      maxDays: 60,
      regulator: 'Commissioner of Information',
      law: 'Freedom of Information Act 1999',
      requirements: [
        'Respond within 30 calendar days of receipt',
        'May extend by 30 days if necessary',
        'Verify requester identity and authority',
        'Provide information in accessible form',
        'Standard fee applies for copies'
      ]
    }
  };

  const rules = jurisdictionRules[jurisdiction] || jurisdictionRules['JM'];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          Compliance Reminders — {rules.flag} {rules.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Statutory Deadlines */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                  Standard Deadline
                </p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100 mt-1">
                  {rules.standardDays}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">calendar days</p>
              </div>
            </div>
          </div>

          <div className="p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                  Extension Days
                </p>
                <p className="text-2xl font-bold text-amber-900 dark:text-amber-100 mt-1">
                  +{rules.extensionDays}
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">if complex</p>
              </div>
            </div>
          </div>

          <div className="p-3 rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                  Maximum Total
                </p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100 mt-1">
                  {rules.maxDays}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">with extension</p>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Reference */}
        <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">
            Legal Framework
          </p>
          <div className="space-y-1 text-sm text-slate-700 dark:text-slate-300">
            <p>
              <span className="font-medium">Law:</span> {rules.law}
            </p>
            <p>
              <span className="font-medium">Regulator:</span> {rules.regulator}
            </p>
          </div>
        </div>

        {/* Key Requirements */}
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
            Key Requirements:
          </p>
          <ul className="space-y-2">
            {rules.requirements.map((req, idx) => (
              <li key={idx} className="flex gap-2 text-sm text-slate-700 dark:text-slate-300">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>{req}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

export default ComplianceRemindersWidget;
